import { DestroyRef, Signal, signal } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { combineLatest, EMPTY, merge, Observable, of, Subject } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  finalize,
  map,
  skip,
  startWith,
  switchMap,
  takeUntil,
} from 'rxjs/operators';
import {
  ACCOUNT_NAME_ENQUIRY_FALLBACK_MESSAGE,
  AccountNameEnquiryResult,
  VerifyAccountRequest,
} from '../models/account-name-enquiry';

/** Performs one enquiry. Must emit exactly once and should never error. */
export type AccountNameVerifyFn = (
  request: VerifyAccountRequest,
) => Observable<AccountNameEnquiryResult>;

export interface AccountNameEnquiryOptions {
  /** Control holding the NUBAN. */
  readonly accountNumber: AbstractControl;
  /** Control holding whatever identifies the bank to `verify` (normally the NIP code). */
  readonly bankCode: AbstractControl;
  /** Control that receives the resolved name. Never user-editable. */
  readonly accountName: AbstractControl;
  readonly verify: AccountNameVerifyFn;
  /**
   * Ties the subscription to the host component or dialog. `destroy()` on the
   * handle exists for controls that die earlier than the host (FormArray rows).
   */
  readonly destroyRef: DestroyRef;
  /** While this returns false no enquiry is issued (e.g. TSA beneficiaries). */
  readonly enabled?: () => boolean;
  /** Overrides the "pair is complete" test. Default: 10 digits plus a non-empty bank. */
  readonly isPairReady?: (accountNumber: string, bankCode: string) => boolean;
  readonly debounceMs?: number;
  readonly fallbackMessage?: string;
}

export interface AccountNameEnquiryHandle {
  /** True while an enquiry is in flight. */
  readonly resolving: Signal<boolean>;
  /** True when the last enquiry finished without a name. */
  readonly failed: Signal<boolean>;
  readonly errorMessage: Signal<string | null>;
  /** Mirrors the resolved name so templates need not read the control. */
  readonly accountName: Signal<string>;
  /** Re-runs the enquiry for the current pair, bypassing `distinctUntilChanged`. */
  readonly retry: () => void;
  /**
   * Seeds an already-trusted name (edit mode) **without** issuing an enquiry.
   * Call it in the same synchronous block as the patch that fills the pair.
   */
  readonly prime: (seed: {
    accountNumber: string;
    bankCode: string;
    accountName: string;
  }) => void;
  /** Clears the name and status without issuing an enquiry (e.g. NUBAN to TSA). */
  readonly reset: () => void;
  /** Tears down the subscription. Idempotent; also runs on host destroy. */
  readonly destroy: () => void;
}

/**
 * Convenience factory so call sites don't repeat the lambda.
 *
 * Takes the service structurally rather than importing `BankService`, which
 * keeps this file free of any HTTP dependency and unit-testable with a stub.
 */
export function accountNameVerifier(
  service: {
    resolveAccountName(
      baseUrl: string,
      request: VerifyAccountRequest,
    ): Observable<AccountNameEnquiryResult>;
  },
  baseUrl: string,
): AccountNameVerifyFn {
  return (request) => service.resolveAccountName(baseUrl, request);
}

const DEFAULT_NUBAN_PATTERN = /^\d{10}$/;

const asText = (value: unknown): string =>
  value === null || value === undefined ? '' : String(value).trim();

const pairKey = (accountNumber: string, bankCode: string): string =>
  `${accountNumber}|${bankCode}`;

/**
 * Wires an account-number + bank pair to an API-resolved account name.
 *
 * Layout-agnostic — it only touches the three controls, so it works with
 * `app-input`, a raw `<input>`, and inside nested FormArrays. Because it takes
 * an explicit `DestroyRef` it can be called outside an injection context (e.g.
 * from a `addBeneficiaryToProduct()` handler).
 */
export function wireAccountNameEnquiry(
  options: AccountNameEnquiryOptions,
): AccountNameEnquiryHandle {
  const {
    accountNumber: accountNumberControl,
    bankCode: bankCodeControl,
    accountName: accountNameControl,
    verify,
    destroyRef,
    enabled = () => true,
    isPairReady = (accountNumber, bankCode) =>
      DEFAULT_NUBAN_PATTERN.test(accountNumber) && bankCode.length > 0,
    debounceMs = 300,
    fallbackMessage = ACCOUNT_NAME_ENQUIRY_FALLBACK_MESSAGE,
  } = options;

  const resolving = signal(false);
  const failed = signal(false);
  const errorMessage = signal<string | null>(null);
  const accountName = signal(asText(accountNameControl.value));

  const retries = new Subject<void>();
  const destroyed$ = new Subject<void>();
  /** Pair key the current name is trusted for; null when unresolved. */
  let resolvedKey: string | null = null;
  let isDestroyed = false;

  const writeName = (value: string): void => {
    // `emitEvent: false` — nothing subscribes to the name control, and a silent
    // write keeps host `form.valueChanges` wiring from re-entering. Ancestor
    // validity is still recomputed, so `form.invalid` stays correct.
    accountNameControl.setValue(value, { emitEvent: false });
    accountName.set(value);
  };

  const currentPair = (): VerifyAccountRequest => ({
    accountNumber: asText(accountNumberControl.value),
    bankCode: asText(bankCodeControl.value),
  });

  const pairChanges$ = combineLatest([
    accountNumberControl.valueChanges.pipe(
      startWith(accountNumberControl.value),
    ),
    bankCodeControl.valueChanges.pipe(startWith(bankCodeControl.value)),
  ]).pipe(
    map(([accountNumberValue, bankCodeValue]) => ({
      accountNumber: asText(accountNumberValue),
      bankCode: asText(bankCodeValue),
      forced: false,
    })),
    // Drop the synchronous snapshot `startWith` produces at subscribe time. The
    // `startWith` pair is required — without it `combineLatest` would not emit
    // until BOTH controls change, so a form with a pre-filled bank would never
    // fire when the user types only the account number. `skip(1)` eats exactly
    // that snapshot, since nothing else can emit in the same tick.
    skip(1),
    debounceTime(debounceMs),
    distinctUntilChanged(
      (a, b) => a.accountNumber === b.accountNumber && a.bankCode === b.bankCode,
    ),
  );

  // Retries are merged in AFTER `distinctUntilChanged` so it can never swallow
  // them — that is the bug in the current hand-rolled `retryAccountLookup()`,
  // where re-emitting the identical pair is silently dropped.
  const retries$ = retries.pipe(
    map(() => ({ ...currentPair(), forced: true })),
  );

  merge(pairChanges$, retries$)
    .pipe(
      switchMap(({ accountNumber, bankCode, forced }) => {
        const key = pairKey(accountNumber, bankCode);

        // Already trusted for this exact pair (typed or primed): don't clear and
        // don't refetch. This is what suppresses the edit-mode enquiry storm.
        if (!forced && key === resolvedKey && accountName()) {
          return EMPTY;
        }

        // Any other change invalidates the previously resolved name.
        resolvedKey = null;
        failed.set(false);
        errorMessage.set(null);
        writeName('');

        if (!enabled() || !isPairReady(accountNumber, bankCode)) {
          return EMPTY;
        }

        resolving.set(true);
        return verify({ accountNumber, bankCode }).pipe(
          // Defence in depth: `verify` is contractually non-throwing, but a
          // custom one may slip.
          catchError(() =>
            of<AccountNameEnquiryResult>({
              resolved: false,
              message: fallbackMessage,
            }),
          ),
          map((result) => ({ key, result })),
          // Runs on completion AND on switchMap cancellation. switchMap
          // unsubscribes the previous inner before projecting the new one, so
          // the old `resolving(false)` always precedes the new `resolving(true)`
          // — no false "idle" flicker between keystrokes.
          finalize(() => resolving.set(false)),
        );
      }),
      takeUntil(destroyed$),
    )
    .subscribe(({ key, result }) => {
      if (result.resolved && result.accountName) {
        resolvedKey = key;
        failed.set(false);
        errorMessage.set(null);
        writeName(result.accountName);
        return;
      }
      resolvedKey = null;
      failed.set(true);
      errorMessage.set(
        result.resolved ? fallbackMessage : result.message || fallbackMessage,
      );
      writeName('');
    });

  function destroy(): void {
    if (isDestroyed) return;
    isDestroyed = true;
    destroyed$.next();
    destroyed$.complete();
    retries.complete();
    unregisterHostTeardown();
  }

  const unregisterHostTeardown = destroyRef.onDestroy(() => destroy());

  return {
    resolving: resolving.asReadonly(),
    failed: failed.asReadonly(),
    errorMessage: errorMessage.asReadonly(),
    accountName: accountName.asReadonly(),
    retry: () => {
      if (!isDestroyed) retries.next();
    },
    prime: (seed) => {
      resolvedKey = pairKey(asText(seed.accountNumber), asText(seed.bankCode));
      failed.set(false);
      errorMessage.set(null);
      resolving.set(false);
      writeName(asText(seed.accountName));
    },
    reset: () => {
      resolvedKey = null;
      failed.set(false);
      errorMessage.set(null);
      resolving.set(false);
      writeName('');
    },
    destroy,
  };
}
